import { AmplifyUser } from "@aws-amplify/ui";

// Must mirror Cognito User Group names exactly
export enum UserGroup {
  SuperUser = "SuperUser",
  DevTeam = "DevTeam",
  Coaches = "Coaches",
  ResearchAdmin = "ResearchAdmin",
  ReboundingDashboardUsers = "ReboundingDashboardUsers",
}

export function getUserGroups(user: AmplifyUser): string[] {
  try {
    return (
      user.getSignInUserSession()?.getIdToken().payload["cognito:groups"] ?? []
    );
  } catch {
    return [];
  }
}

/**
 * Check if user is member of group from Cognito payload
 * Allow super user access to all. Deny non-super user dev team access.
 * @param user
 * @param checkGroups any number of group names
 * @returns true if user is a member of any of the groups or is a super user
 */
export function isGroupMember(
  user?: AmplifyUser,
  ...checkGroups: UserGroup[]
): boolean {
  if (!user) return false;
  const userGroups = getUserGroups(user);
  return (
    userGroups.includes(UserGroup.SuperUser) ||
    (process.env.NODE_ENV === "development" &&
      userGroups.includes(UserGroup.DevTeam)) ||
    checkGroups.some((group) => userGroups.includes(group))
  );
}

export const isCoach = (user?: AmplifyUser): boolean =>
  isGroupMember(user, UserGroup.Coaches);

// Research wiki role. A single `ResearchAdmin` group controls write
// access — create / edit / publish / unpublish / delete. Reading is
// universal, no role check on the nav entry.
//
// Uses `isGroupMember` so the `DevTeam` dev fallback applies when running
// locally. Heads-up: the backend has the same fallback keyed on
// `FLASK_ENV=development`, so a CRA dev server pointed at a non-dev Flask
// will show Edit buttons that 403 on save. Run both ends in dev mode (or
// add yourself to `ResearchAdmin` / `SuperUser`) to avoid that mismatch.
export const isResearchAdmin = (user?: AmplifyUser): boolean =>
  isGroupMember(user, UserGroup.ResearchAdmin);
